import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';
import RatingField from 'components/new_ui/Form/RatingField';
import HorizontalTextField from 'components/new_ui/Form/HorizontalTextField';
import { Rating } from 'react-simple-star-rating';
import axios from 'axios';

import routes from 'app/routes';
import Alert from 'components/new_ui/Alerts/alert';
import IconWithText from 'components/Widgets/IconWithText';
import SubmitButton from 'components/Widgets/SubmitButton';

import Actions from 'components/Members/Programs/Actions';

export default function Form({ coachFeedback }) {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [overallRating, setOverallRating] = useState(0);

  const ratingDescription = (rating) => {
    switch (Math.round(rating)) {
      case 1:
        return 'Poor';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Very good';
      case 5:
        return 'Excellent';
      default:
        return 'N/A';
    }
  };

  const formSchema = Yup.object().shape({
    feedback: Yup.string().trim().required('Please tell us something you liked about your coach.'),
    feedback_private: Yup.string().trim().required('Please tell us something you would like to see improved.'),
  });

  const onSubmitHundler = (values, { setSubmitting, _setErrors }) => {
    if (Object.keys(values.questions).some((key) => values.questions[key] === undefined
        || values.questions[key] === null)) {
      setSubmitting(false);

      Alert({
        text: 'Please answer all the questions to save your feedback.',
      });
    } else if (Object.keys(values.questions).every((key) => values.questions[key] === -1)) {
      setSubmitting(false);

      Alert({
        text: 'Please rate at least one question using stars. N/A rating is not allowed to be set for all questions.',
      });
    } else {
      const params = {
        rating: { ...values },
      };

      axios
        .post(routes.api.programs.coachFeedback(programId), params)
        .then((response) => {
          setSubmitting(false);
          if (response.data.data.attributes.is_share_needed) {
            Actions.googleShare(response.data.data.attributes.stare_feedback_action_id, () => {
              navigate(routes.members.programs.show(programId));
            });
          } else {
            navigate(routes.members.programs.show(programId));
          }
        });
    }
  };

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={{
        questions: Object.fromEntries(
          coachFeedback.attributes.questions.map((question) => [question.id, question.rate]),
        ),
        feedback: coachFeedback.attributes.feedback.answer,
        feedback_private: coachFeedback.attributes.feedback_private.answer,
      }}
      onSubmit={onSubmitHundler}
    >
      {({ values, isSubmitting }) => (
        <FormikForm className="needs-validation" noValidate="">
          <section>
            <Row>
              {coachFeedback.attributes.questions.map((question) => (
                <Col key={question.id} lg={6}>
                  <RatingField
                    id={`question_${question.id}`}
                    name={`questions['${question.id}']`}
                    label={<span className="fw-semibold">{question.title}</span>}
                    disabled={isSubmitting}
                    onClick={() => {
                      const result = Object.keys(values.questions).filter((key) => (
                        values.questions[key] !== undefined
                        && values.questions[key] !== null && values.questions[key] !== -1
                      )).map((key) => values.questions[key]);

                      if (result.length === 0) {
                        setOverallRating(0);
                      } else {
                        setOverallRating(result.reduce((acc, val) => acc + val, 0) / result.length);
                      }
                    }}
                  />
                </Col>
              ))}
            </Row>

            <Row>
              <Col lg={6}>
                <HorizontalTextField
                  as="textarea"
                  id="feedback"
                  name="feedback"
                  label={(
                    <>
                      <span className="fw-semibold">
                        {coachFeedback.attributes.feedback.question}
                      </span>

                      <br />

                      <small>
                        {coachFeedback.attributes.feedback.description}
                      </small>
                    </>
                  )}
                  rows={4}
                  disabled={isSubmitting}
                />
              </Col>

              <Col lg={6}>
                <HorizontalTextField
                  as="textarea"
                  id="feedback_private"
                  name="feedback_private"
                  label={(
                    <>
                      <span className="fw-semibold">
                        {coachFeedback.attributes.feedback_private.question}
                      </span>

                      <br />

                      <small>
                        {coachFeedback.attributes.feedback_private.description}
                      </small>
                    </>
                  )}
                  rows={4}
                  disabled={isSubmitting}
                />
              </Col>
            </Row>
          </section>

          <footer className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center text-center">
              <span className="mt-md-1">
                Overall rating:

                <span className="d-block d-md-inline mt-2 mt-md-0 mx-md-3 fw-semibold">
                  {overallRating === 0 ? (
                    <>
                      &mdash;
                    </>
                  ) : (
                    <>
                      {Number.parseFloat(overallRating).toFixed(1)}
                      {' '}
                      (
                      {ratingDescription(overallRating)}
                      )
                    </>
                  )}
                </span>
              </span>

              <Rating
                initialValue={Math.round(overallRating)}
                emptyIcon={<IconWithText iconClassName="star-filled-icon bg-gray-300" />}
                fillIcon={<IconWithText iconClassName="star-filled-icon bg-tertiary-600" />}
                className="d-none d-lg-inline-block"
                emptyClassName="d-flex"
                fillClassName="d-flex"
                readonly
              />
            </div>

            <SubmitButton loading={isSubmitting}>
              Submit
            </SubmitButton>
          </footer>
        </FormikForm>
      )}
    </Formik>
  );
}

Form.propTypes = {
  coachFeedback: PropTypes.object.isRequired,
};
